import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Sparkles } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';
import ImageUploadField from './ImageUploadField';

const emptyForm = { title: '', image_url: '', size: 'small', style: '', status: 'available' };

const FlashManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/flash`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setMode('create');
    setForm(emptyForm);
    setCurrent(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setMode('edit');
    setCurrent(item);
    setForm({
      title: item.title || '',
      image_url: item.image_url || '',
      size: item.size || 'small',
      style: item.style || '',
      status: item.status || 'available',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this flash design?')) return;
    try {
      await axios.delete(`${API}/flash/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await axios.post(`${API}/flash`, form, getAuthHeader());
        toast.success('Flash design added');
      } else {
        await axios.put(`${API}/flash/${current.id}`, form, getAuthHeader());
        toast.success('Flash design updated');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="flash-content">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <Sparkles size={28} /> Flash Gallery
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-flash-btn"
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gray-800"
        >
          <Plus size={16} /> Add Flash Design
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {items.map((item) => (
          <div key={item.id} className="bg-white border border-gray-200 overflow-hidden group" data-testid={`flash-item-${item.id}`}>
            <div className="relative aspect-square">
              <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                <button onClick={() => openEdit(item)} className="p-2 bg-white rounded-full hover:bg-gray-100" data-testid={`edit-flash-${item.id}`}>
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600" data-testid={`delete-flash-${item.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-heading font-bold">{item.title}</h3>
              <div className="flex gap-2 mt-2">
                <span className="font-mono text-[10px] uppercase bg-gray-100 px-2 py-1">{item.size}</span>
                <span className="font-mono text-[10px] uppercase bg-gray-100 px-2 py-1">{item.style}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`${mode === 'create' ? 'Add' : 'Edit'} Flash Design`}
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.title || !form.style || !form.image_url}
            data-testid="flash-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Add Flash Design' : 'Save Changes'}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="flash-form-title"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Image *"
            testId="flash-image"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Size *</label>
              <select
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                data-testid="flash-form-size"
                className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Style *</label>
              <input
                type="text"
                value={form.style}
                onChange={(e) => setForm({ ...form, style: e.target.value })}
                placeholder="e.g., Minimalist"
                data-testid="flash-form-style"
                className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            >
              <option value="available">Available</option>
              <option value="claimed">Claimed</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FlashManager;
